import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { UserSessionSelector } from 'store/UserSession';
import { useHistory } from 'react-router-dom';
import {
    aiSmartContentTypeSelector,
    setAISmartContentType,
} from 'store/AdminClient/AISmartContentType';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import TokenService from "config/token.service";
import axiosPrivate from 'config/axiosPrivate';
import { fetchUser } from "store/UserSession/features";
import { useGuide } from "utils/contexts/GuideContext";
import OnboardingVideoCard from 'components/shared/OnboardingVideoCard';
import "./Sidebar.css";
import SidebarWebsiteDropdown from './SidebarWebsiteDropdown';

function Item({
    to,
    icon: Icon,
    iconSrc,
    label,
    id,
    active,
    onClick,
    right,
    className = "",
    ariaControls,
    ariaExpanded,
    style,
    disabled = false,
}) {
    const Comp = to && !disabled ? Link : "button";

    const commonProps = {
        onClick: disabled ? undefined : onClick,
        id,
        className: `sb-item ${active ? "active" : ""} ${disabled ? "sb-disabled" : ""} ${className}`,
        "aria-current": active ? "page" : undefined,
        "aria-controls": ariaControls,
        "aria-expanded": ariaExpanded,
        "aria-disabled": disabled ? "true" : undefined,
        tabIndex: disabled ? -1 : undefined,
        style
    };

    return (
        <Comp {...(to && !disabled ? { to } : { type: "button" })} {...commonProps}>
            {Icon && <Icon className="sb-ic" aria-hidden="true" style={{ fontSize: '16px' }} />}
            {iconSrc && <img src={iconSrc} alt="" className="sb-ic" style={{ width: '16px', height: '16px' }} />}
            <span className="sb-label">{label}</span>
            {right}
        </Comp>
    );
}

export default function Sidebar() {
    const { t } = useTranslation();
    const { startGuide } = useGuide();
    const { pathname } = useLocation();
    const dispatch = useDispatch();
    let { user } = useSelector(UserSessionSelector);

    const is = (p) => pathname === p;
    const isDashboardPage = pathname === "/AdminClient/AdminMainMenu";

    const currentAISmartType = useSelector(aiSmartContentTypeSelector);

    const isAISmartType = (type) => {
        if (pathname !== "/AdminClient/IA_Smart") return false;
        const current = currentAISmartType || "PRODUCT";
        return current === type;
    };

    const REDACTION_IA_CONTENT_TYPES = [
        "OTHER",
        "PRODUCT",
        "GMB",
        "STORE_LOCATOR",
        "LONG_CATEGORY",
        "CONTENT_IMPROVEMENT",
    ];
    const isRedactionIAContentType = REDACTION_IA_CONTENT_TYPES.includes(
        currentAISmartType || "PRODUCT"
    );
    // Youtube shares IA_Smart / createIAsmarttext but lives under Social Media IA
    const showBriefVideo =
        pathname === "/AdminClient/BriefGenerator" ||
        pathname.startsWith("/AdminClient/createIAbrief");
    const showRedactionIAVideo =
        (pathname === "/AdminClient/IA_Smart" ||
            pathname.startsWith("/AdminClient/createIAsmarttext")) &&
        isRedactionIAContentType;
    const showAgentIAVideo =
        pathname.startsWith("/AdminClient/AgentIA") ||
        pathname.startsWith("/AdminClient/createagentiafromlist") ||
        pathname.startsWith("/AdminClient/CreateAgentIA");
    const showCreateProjectVideo =
        pathname === "/AdminClient/CreateProject" ||
        pathname.startsWith("/AdminClient/CreateProject/") ||
        pathname === "/AdminClient/ProjectsEnCours";
    const showKeywordDiscoveryVideo =
        pathname === "/AdminClient/KeywordDiscovery" ||
        pathname.startsWith("/AdminClient/GenerateKeyword");
    const showBatchWritingVideo =
        pathname === "/AdminClient/DetailsProject" ||
        pathname.startsWith("/AdminClient/DetailsProject/");
    const onboardingVideo = showAgentIAVideo
        ? { src: "/Agent-IA.mp4", titleKey: "Agent IA" }
        : showBriefVideo
            ? { src: "/V1-Générateur-de-brief.mp4", titleKey: "Générateur de brief" }
            : showRedactionIAVideo
                ? { src: "/Redaction-IA.mp4", titleKey: "Rédaction IA" }
                : showCreateProjectVideo
                    ? { src: "/Création-de-projet.mp4", titleKey: "Création de projet" }
                    : showKeywordDiscoveryVideo
                        ? {
                              src: "/V1-Découverte-mots-clés.mp4",
                              titleKey: "Découverte de mots-clés",
                          }
                        : showBatchWritingVideo
                            ? {
                                  src: "/V1-Rédaction-en-lot.mp4",
                                  titleKey: "Rédaction en lot",
                              }
                            : {
                                  src: "/V10-Onboarding-Typix.mp4",
                                  titleKey: "Découvrez Typix Néo en 2 min",
                              };

    // états accordéon
    const [openProjects, setOpenProjects] = useState(false);
    const [openGenerator, setOpenGenerator] = useState(true);
    const [openSeo, setOpenSeo] = useState(false);
    const [openSocial, setOpenSocial] = useState(false);
    const [openWorkspace, setOpenWorkspace] = useState(true);
    const [openUserMenu, setOpenUserMenu] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [showUpdateNotification, setShowUpdateNotification] = useState(true);
    const [subscriptionPlanKey, setSubscriptionPlanKey] = useState('');
    const [featureFlags, setFeatureFlags] = useState({
        translationEnabled: true,
        enrichmentEnabled: true,
        supportEnabled: true,
        briefEnabled: false,
        discoveryEnabled: false,
    });
    const [contentTypeFlags, setContentTypeFlags] = useState({
        OTHER: true,
        PRODUCT: true,
        GMB: true,
        STORE_LOCATOR: true,
        LONG_CATEGORY: true,
        CONTENT_IMPROVEMENT: true,
        YOUTUBE: true,
    });
    const userMenuRef = useRef(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

    const { bills } = useSelector((state) => state.allBills) || {};
    const normalizedSubscriptionPlan = String(subscriptionPlanKey || '').trim().toUpperCase();
    const subscriptionBadgeLabel = normalizedSubscriptionPlan || 'FREE';
    const isFreePlan = !normalizedSubscriptionPlan || normalizedSubscriptionPlan === 'FREE';

    const handleLogout = () => {
        TokenService.removeUser();
        dispatch(fetchUser(null));
    };

    const toggleProjects = () => {
        setOpenProjects((v) => !v);
        setOpenGenerator(false);
        setOpenSocial(false);
    };
    const toggleGenerator = () => {
        setOpenGenerator((v) => !v);
        setOpenProjects(false);
        setOpenSocial(false);
        setOpenSeo(false);
    };
    const toggleSocial = () => {
        setOpenSocial((v) => !v);
        setOpenProjects(false);
        setOpenGenerator(false);
    };
    const toggleSeo = () => {
        setOpenSeo((v) => !v);
    };
    const toggleWorkspace = () => {
        setOpenWorkspace((v) => !v);
    };
    const toggleSidebar = () => {
        setIsCollapsed((v) => !v);
    };

    useEffect(() => {
        let isMounted = true;

        const normalizeContentType = (value) =>
            String(value || '')
                .trim()
                .toUpperCase()
                .replace(/\s+/g, '_');

        const resolveEnabledContentTypes = (data) => {

            const listFromArray =
                (Array.isArray(data?.availableContentTypes) && data.availableContentTypes) ||
                (Array.isArray(data?.enabledContentTypes) && data.enabledContentTypes) ||
                (Array.isArray(data?.contentTypes) && data.contentTypes) ||
                null;

            if (listFromArray) {
                const allowed = new Set(listFromArray.map(normalizeContentType).filter(Boolean));
                return {
                    OTHER: allowed.has('OTHER'),
                    PRODUCT: allowed.has('PRODUCT'),
                    GMB: allowed.has('GMB'),
                    STORE_LOCATOR: allowed.has('STORE_LOCATOR'),
                    LONG_CATEGORY: allowed.has('LONG_CATEGORY'),
                    CONTENT_IMPROVEMENT: allowed.has('CONTENT_IMPROVEMENT'),
                    YOUTUBE: allowed.has('YOUTUBE'),
                };
            }

            const byObject = data?.contentTypesEnabled || data?.contentTypeFlags || null;
            if (byObject && typeof byObject === 'object') {
                return {
                    OTHER: byObject.OTHER !== false,
                    PRODUCT: byObject.PRODUCT !== false,
                    GMB: byObject.GMB !== false,
                    STORE_LOCATOR: byObject.STORE_LOCATOR !== false,
                    LONG_CATEGORY: byObject.LONG_CATEGORY !== false,
                    CONTENT_IMPROVEMENT: byObject.CONTENT_IMPROVEMENT !== false,
                    YOUTUBE: byObject.YOUTUBE !== false,
                };
            }

            return {
                OTHER: true,
                PRODUCT: true,
                GMB: true,
                STORE_LOCATOR: true,
                LONG_CATEGORY: true,
                CONTENT_IMPROVEMENT: data?.enrichmentEnabled !== false,
                YOUTUBE: true,
            };
        };

        const fetchCompanyQuotas = async () => {
            try {
                const { data } = await axiosPrivate.get('/project/company-quotas');
                if (!isMounted) return;
                setFeatureFlags({
                    translationEnabled: data?.translationEnabled !== false,
                    enrichmentEnabled: data?.enrichmentEnabled !== false,
                    supportEnabled: data?.supportEnabled !== false,
                    briefEnabled: data?.briefEnabled === true,
                    discoveryEnabled: data?.discoveryEnabled === true,
                });
                setSubscriptionPlanKey(data?.planKey || '');
                setContentTypeFlags(resolveEnabledContentTypes(data));
            } catch (error) {
                if (isMounted) {
                    setFeatureFlags({
                        translationEnabled: true,
                        enrichmentEnabled: true,
                        supportEnabled: true,
                        briefEnabled: false,
                        discoveryEnabled: false,
                    });
                    setContentTypeFlags({
                        OTHER: true,
                        PRODUCT: true,
                        GMB: true,
                        STORE_LOCATOR: true,
                        LONG_CATEGORY: true,
                        CONTENT_IMPROVEMENT: true,
                        YOUTUBE: true,
                    });
                    setSubscriptionPlanKey('');
                }
            }
        };

        fetchCompanyQuotas();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (openUserMenu && userMenuRef.current) {
            const rect = userMenuRef.current.getBoundingClientRect();
            setMenuPosition({
                top: rect.bottom - 115,
                left: rect.right + 4
            });
        }
    }, [openUserMenu]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!openUserMenu) return;

            const dropdown = document.querySelector('[data-user-menu-dropdown]');
            const isClickInsideDropdown = dropdown && dropdown.contains(event.target);
            const isClickInsideButton = userMenuRef.current && userMenuRef.current.contains(event.target);

            if (!isClickInsideDropdown && !isClickInsideButton) {
                setOpenUserMenu(false);
            }
        };

        if (openUserMenu) {
            const timeoutId = setTimeout(() => {
                document.addEventListener('mousedown', handleClickOutside);
            }, 0);

            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [openUserMenu]);

    return (
        <aside className={`sb-root ${isCollapsed ? 'sb-root--collapsed' : ''}`} aria-label="Sidebar navigation">
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexDirection: isCollapsed ? 'column' : 'row',
                    padding: '0 6px 0 20px',
                    height: '40px',
                    marginBottom: 0,
                }}
            >
                <Link to="/AdminClient/AdminMainMenu" style={{ display: 'flex', alignItems: 'center', gap: isCollapsed ? '0' : '110px' }}>
                    <div className="sb-logo-plan-wrap">
                        <img
                            src="Images/new1-logo.png"
                            alt="TYPIX logo"
                            className="logo-m"
                            style={{ maxWidth: '80px', height: 'auto', display: 'block' }}
                        />
                        {!isCollapsed && (
                            <span className="sb-subscription-badge sb-subscription-badge--header">
                                {subscriptionBadgeLabel}
                            </span>
                        )}
                    </div>

                </Link>
                {!isCollapsed && (
                    <img
                        src="Images/icon-menu/icone-close-menu.png"
                        alt="menu icon"
                        onClick={toggleSidebar}
                        style={{ width: '35px', height: '33px', cursor: 'pointer' }}
                    />
                )}
                {isCollapsed && (
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '10px' }}>

                        <img
                            src="Images/icon-menu/icon-open-menu.png"
                            alt="menu icon"
                            onClick={toggleSidebar}
                            style={{ width: '35px', height: '33px', cursor: 'pointer', marginRight: isCollapsed ? '10px' : '0' }}
                        />
                    </div>
                )}
            </div>


            <SidebarWebsiteDropdown isCollapsed={isCollapsed} />

            <div className="sb-section" style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0, padding: '0 14px', gap: '4px' }}>
                <div className="sb-section-title" >
                    ANALYSES
                </div>
                <nav className="sb-nav">
                    <Item
                        to="/AdminClient/AdminMainMenu"
                        icon={() => <img src="/Images/sidebar-icons/dashboard.png" alt="Dashboard" className="sb-menu-item-icon" />}
                        label={t("Tableau de bord")}
                        active={is("/AdminClient/AdminMainMenu")}
                        className="sb-menu-item"
                    />

             {/* <Item
                        to="/AdminClient/Performance"
                        icon={() => (<img src="/Images/sidebar-icons/perf.png" alt="Performances" className="sb-menu-item-icon" />)}
                        label={t("Performances")}
                        active={is("/AdminClient/Performance")}
                        right={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <LockOutlinedIcon className="sb-lock" aria-hidden="true" />
                            </div>
                        }
                        className="sb-menu-item sb-disabled"
                    /> */}
                

                    <div
                        className="sb-menu-item"
                        onClick={toggleWorkspace}

                    >
                        {!isCollapsed && (
                            <img
                                src="Images/icon-menu/arrow-menu.png"
                                alt="arrow menu"
                                style={{
                                    width: '8px',
                                    height: '8px',
                                    transform: openWorkspace ? 'rotate(0deg)' : 'rotate(-90deg)',
                                    transition: 'transform 0.2s ease'
                                }}
                            />)}
                        <span className="sb-workspace-title-full" style={{ color: '#818181' }} >{t("Espace de travail AI")}</span>
                        <span className="sb-workspace-title-short">{t("AI")}</span>
                    </div>

                    <div style={{ display: openWorkspace || isCollapsed ? 'block' : 'none' }}>
                        <Item
                            to="/AdminClient/AgentIAList"
                            icon={() => <img src="/Images/sidebar-icons/agent_ai.png" alt="Agent IA" className="sb-menu-item-icon" />}
                            label={t("Agent IA")}
                            active={is("/AdminClient/AgentIAList")}
                            id="agent-IA"
                        />
                        {/* Générateur IA (collapsible) */}
                        <Item
                            icon={() => <img src="/Images/sidebar-icons/redact_ai.png" alt="Rédaction IA" className="sb-menu-item-icon" />} label={t("Rédaction IA")}
                            onClick={toggleGenerator}
                            disabled={isFreePlan}
                            right={
                                isFreePlan ? (
                                    <LockOutlinedIcon className="sb-lock" aria-hidden="true" />
                                ) : (
                                    <ExpandMoreIcon
                                        className={`sb-caret ${openGenerator ? "open" : ""}`}
                                        aria-hidden="true"
                                    />
                                )
                            }
                            ariaControls="submenu-generator"
                            ariaExpanded={openGenerator}
                            id="IA-Generator"
                        />
                        <div
                            id="submenu-generator"
                            className={`sb-subwrap ${openGenerator || isCollapsed ? "open" : ""}`}
                            role="region"
                            aria-hidden={!openGenerator && !isCollapsed}
                            style={{ borderLeft: isCollapsed ? 'none' : '1px solid rgba(0, 0, 0, 0.08)' }}
                        >
                            <div className="sb-connector" />
                            <div className="sb-sub"  >
                                {/* Contenu SEO : une seule page IA_Smart, type via Redux */}
                                <Item
                                    to={contentTypeFlags.OTHER ? "/AdminClient/IA_Smart" : undefined}
                                    onClick={contentTypeFlags.OTHER ? () => dispatch(setAISmartContentType("OTHER")) : undefined}
                                    active={isAISmartType("OTHER")}
                                    label={t("Contenu web")}
                                    className="sb-subitem"
                                    icon={() => <img src="/Images/sidebar-icons/page_categ.png" alt="Contenu web" className="sb-menu-item-icon" />}
                                    disabled={!contentTypeFlags.OTHER}
                                />
                                <Item
                                    to={contentTypeFlags.PRODUCT ? "/AdminClient/IA_Smart" : undefined}
                                    onClick={contentTypeFlags.PRODUCT ? () => dispatch(setAISmartContentType("PRODUCT")) : undefined}
                                    active={isAISmartType("PRODUCT")}
                                    label={t("Fiche produit")}
                                    className="sb-subitem"
                                    icon={() => <img src="/Images/sidebar-icons/fiche_produit.png" alt="Fiche produit" className="sb-menu-item-icon" />}
                                    disabled={!contentTypeFlags.PRODUCT}
                                />
                                <Item
                                    to={contentTypeFlags.GMB ? "/AdminClient/IA_Smart" : undefined}
                                    onClick={contentTypeFlags.GMB ? () => dispatch(setAISmartContentType("GMB")) : undefined}
                                    label={t("Fiche GMB")}
                                    active={isAISmartType("GMB")}
                                    className="sb-subitem"
                                    icon={() => <img src="/Images/sidebar-icons/fiche_gmb.png" alt="Fiche GMB" className="sb-menu-item-icon" />}
                                    disabled={!contentTypeFlags.GMB}
                                />
                                <Item
                                    to={contentTypeFlags.STORE_LOCATOR ? "/AdminClient/IA_Smart" : undefined}
                                    onClick={contentTypeFlags.STORE_LOCATOR ? () => dispatch(setAISmartContentType("STORE_LOCATOR")) : undefined}
                                    label={t("Store locator")}
                                    active={isAISmartType("STORE_LOCATOR")}
                                    className="sb-subitem"
                                    icon={() => <img src="/Images/sidebar-icons/store_locator.png" alt="Store locator" className="sb-menu-item-icon" />}
                                    disabled={!contentTypeFlags.STORE_LOCATOR}
                                />
                                <Item
                                    to={contentTypeFlags.LONG_CATEGORY ? "/AdminClient/IA_Smart" : undefined}
                                    onClick={contentTypeFlags.LONG_CATEGORY ? () => dispatch(setAISmartContentType("LONG_CATEGORY")) : undefined}
                                    label={t("Page catégorie")}
                                    active={isAISmartType("LONG_CATEGORY")}
                                    className="sb-subitem"
                                    icon={() => <img src="/Images/sidebar-icons/page_categ.png" alt="Page catégorie" className="sb-menu-item-icon" />}
                                    disabled={!contentTypeFlags.LONG_CATEGORY}
                                />
                                <Item
                                    to={featureFlags.enrichmentEnabled ? "/AdminClient/IA_Smart" : undefined}
                                    onClick={featureFlags.enrichmentEnabled ? () => dispatch(setAISmartContentType("CONTENT_IMPROVEMENT")) : undefined}
                                    label={t("Enrichissement")}
                                    active={isAISmartType("CONTENT_IMPROVEMENT")}
                                    className="sb-subitem"
                                    icon={() => <img src="/Images/sidebar-icons/enrichissement.png" alt="Enrichissement SEO" className="sb-menu-item-icon" />}
                                    disabled={!featureFlags.enrichmentEnabled}
                                />

                                <Item
                                    to={featureFlags.briefEnabled ? "/AdminClient/BriefGenerator" : undefined}
                                    label={t("Brief generator")}
                                    active={
                                        is('/AdminClient/BriefGenerator') ||
                                        pathname.startsWith('/AdminClient/createIAbrief')
                                    }
                                    className="sb-subitem"
                                    icon={() => <img src="/Images/sidebar-icons/brief.png" alt="Brief generator" className="sb-menu-item-icon" />}
                                    disabled={!featureFlags.briefEnabled}
                                    right={!featureFlags.briefEnabled ? <LockOutlinedIcon className="sb-lock" aria-hidden="true" style={{ marginLeft: '-8px' }} /> : undefined}
                                />
                            </div>
                        </div>
                        <Item
                            icon={() => <img src="/Images/sidebar-icons/social_media_ai.png" alt="Social Media IA" className="sb-menu-item-icon" />}
                            label={t("Social Media IA")}
                            onClick={toggleSocial}
                            disabled={isFreePlan}
                            right={
                                isFreePlan ? (
                                    <LockOutlinedIcon className="sb-lock" aria-hidden="true" />
                                ) : (
                                    <ExpandMoreIcon
                                        className={`sb-caret ${openSocial ? "open" : ""}`}
                                        aria-hidden="true"
                                    />
                                )
                            }
                            ariaControls="submenu-media"
                            ariaExpanded={openSocial}
                        />
                        <div
                            id="submenu-media"
                            className={`sb-subwrap ${openSocial || isCollapsed ? "open" : ""}`}
                            role="region"
                            aria-hidden={!openSocial && !isCollapsed}
                        >
                            <div className="sb-connector" />
                            <div className="sb-sub">
                                <Item
                                    to={contentTypeFlags.YOUTUBE ? "/AdminClient/IA_Smart" : undefined}
                                    onClick={contentTypeFlags.YOUTUBE ? () => dispatch(setAISmartContentType("YOUTUBE")) : undefined}
                                    active={isAISmartType("YOUTUBE")}
                                    label={t("Youtube")}
                                    className="sb-subitem"
                                    disabled={!contentTypeFlags.YOUTUBE}
                                />
                                <Item
                                    to="#"
                                    label={t("Tiktok")}
                                    className="sb-subitem sb-disabled"
                                    right={<LockOutlinedIcon className="sb-lock" aria-hidden="true" />}
                                />
                                <Item
                                    to="#"
                                    label={t("Linkedin")}
                                    className="sb-subitem sb-disabled"
                                    right={<LockOutlinedIcon className="sb-lock" aria-hidden="true" />}
                                />
                            </div>
                        </div>
                        <Item
                            to={featureFlags.translationEnabled ? "/AdminClient/TraductionIA" : undefined}
                            icon={() => <img src="/Images/sidebar-icons/traduction.png" alt="Traduction IA" className="sb-menu-item-icon" />}
                            label={t("Traduction IA")}
                            active={is("/AdminClient/TraductionIA")}
                            id="IA-translate"
                            disabled={!featureFlags.translationEnabled}
                        />
                       
                    </div>
   <Item
                        to={featureFlags.discoveryEnabled ? "/AdminClient/KeywordDiscovery" : undefined}
                        icon={() => (<img src="/Images/icon-keyword.png" alt="Keyword Discovery" className="sb-menu-item-icon" />)}
                        label={t("Découverte mots-clés")}
                        active={
                          is("/AdminClient/KeywordDiscovery") ||
                          pathname.startsWith("/AdminClient/GenerateKeyword")
                        }
                        className="sb-menu-item"
                        disabled={!featureFlags.discoveryEnabled}
                        right={!featureFlags.discoveryEnabled ? <LockOutlinedIcon className="sb-lock" aria-hidden="true" /> : undefined}
                    />
                    <div className="sb-section-title" >
                        PROJETS
                    </div>

                    <Item
                        to="/AdminClient/ProjectsEnCours"
                        icon={() => <img src="/Images/sidebar-icons/projet.png" alt="Projet" className="sb-menu-item-icon" />}
                        label={t("Projets")}
                        active={is("/AdminClient/ProjectsEnCours")}
                        id="project-menu"
                        className="sb-menu-item"
                    />
                    {/* Sous-menu Projets - mis en commentaire
                    <div
                        id="submenu-projects"
                        className={`sb-subwrap ${openProjects || isCollapsed ? "open" : ""}`}
                        role="region"
                        aria-hidden={!openProjects && !isCollapsed}
                    >
                        <div className="sb-connector" />
                        <div className="sb-sub">
                            <Item to="/AdminClient/ProjectsNonAttribuees" label={t("Non attribués")}
                                  active={is("/AdminClient/ProjectsNonAttribuees")} className="sb-subitem" />
                            <Item to="/AdminClient/ProjectsEnCours" label={t("En cours")}
                                  active={is("/AdminClient/ProjectsEnCours")} className="sb-subitem" />
                            <Item to="/AdminClient/ProjectsEnRetard" label={t("En retard")}
                                  active={is("/AdminClient/ProjectsEnRetard")} className="sb-subitem" />
                            <Item to="/AdminClient/ProjectsValides" label={t("Validés")}
                                  active={is("/AdminClient/ProjectsValides")} className="sb-subitem" />
                        </div>
                    </div>
                    */}
                    {/* <Item
                        to="/AdminClient/CreateProject"
                        icon={PostAddIcon}
                        label={t("Ajouter un projet")}
                        active={is("/AdminClient/CreateProject")}
                        id="create-project"
                    /> */}
                </nav>

                <div className="sb-section-title sb-section-title--account">
                    ACCOUNT
                </div>
                {/* <Item to="/AdminClient/MonProfil"
                      icon={ManageAccountsOutlinedIcon}
                      label={t("Mon profil")}
                      active={is("/AdminClient/MonProfil")}
                      id="profil"/> */}
                <Item
                    className="sb-menu-item"
                    to="/AdminClient/GestionUtilisateur"
                    icon={() => <img src="/Images/sidebar-icons/gestion_users.png" alt="Gestion Users" className="sb-menu-item-icon" />}
                    label={t("Mon équipe")}
                    active={is("/AdminClient/GestionUtilisateur")}
                    id="user-management" />
                <Item
                    className="sb-menu-item"
                    to={featureFlags.supportEnabled ? "/AdminClient/Support" : undefined}
                    icon={() => <img src="/Images/sidebar-icons/support.png" alt="Support" className="sb-menu-item-icon" />}

                    label={t("Support")}
                    active={is("/AdminClient/Support")}
                    id="support"
                    disabled={!featureFlags.supportEnabled}
                />
                {isDashboardPage && (
                    <Item
                        className="sb-menu-item"
                        icon={HelpOutlineIcon}
                        label={t("Mon guide")}
                        onClick={startGuide}
                        id="guide" />
                )}
                <Item
                    className="sb-menu-item"
                    to="/AdminClient/Abonnement"
                    icon={() => <img src="/Images/pricing-icon.png" alt="Abonnement" className="sb-menu-item-icon" />}
                    label={t("Abonnement")}
                    active={is("/AdminClient/Abonnement")}
                    id="subscription"
                />

                <div className="sb-section sb-settings">
                    <nav className="sb-nav">

                        {/* <Item
                            icon={HelpOutlineIcon}
                            label={t("Mon guide")}
                            onClick={startGuide}   // <-- 🚀 déclenche le guide de la page active
                            id="guide"
                        /> */}

                        {/* <Item
                            icon={LogoutOutlinedIcon}
                            label={t("Se déconnecter")}
                            onClick={() => {
                                TokenService.removeUser();
                                dispatch(fetchUser(null));
                            }}
                            className="sb-logout"
                        /> */}
                    </nav>
                </div>
            </div>

            {/* {showUpdateNotification && (
                <div className="sb-update-notification" style={{
                    margin: '0 12px 12px 12px',
                    padding: isCollapsed ? '8px' : '12px',
                    backgroundColor: '#F0F0F0',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: isCollapsed ? 'row' : 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    {!isCollapsed ? (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <img 
                                        src="Images/icon-menu/update-icon.png" 
                                        alt="update icon" 
                                        style={{ width: '15px', height: '15px' }}
                                    />
                                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>{t("Mise à jour 1.2")} 🎉</span>
                                </div>
                                <CloseIcon 
                                    style={{ fontSize: '18px', cursor: 'pointer', color: '#6b7280' }}
                                    onClick={() => setShowUpdateNotification(false)}
                                />
                            </div>
                            <p style={{ 
                                fontSize: '12px', 
                                color: '#131313', 
                                margin: '0 0 8px 0',
                                lineHeight: '1.4',
                                width: '100%'
                            }}>
                                {t("Découvrez les dernières fonctionnalités, optimisations et correctifs.")}
                            </p>
                            <a href="#" style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '4px',
                                fontSize: '12px',
                                color: '#6458F1',
                                textDecoration: 'none',
                                fontWeight: 500,
                                width: '100%'
                            }}>
                                <span style={{ color: '#6458F1' }}>{t("Changelog")} </span>
                                <ArrowForwardIcon style={{ fontSize: '14px', color: '#6458F1' }} />
                            </a>
                        </>
                    ) : (
                        <img 
                            src="Images/icon-menu/update-icon.png" 
                            alt="update icon" 
                            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                            onClick={() => setShowUpdateNotification(false)}
                        />
                    )}
                </div>
            )} */}

            <div className="sb-sidebar-footer">
                <OnboardingVideoCard
                    hidden={isCollapsed}
                    videoSrc={onboardingVideo.src}
                    titleKey={onboardingVideo.titleKey}
                />

                <div className="sb-user-profile" style={{ position: 'relative', marginBottom: '0' }}>
                <div
                    ref={userMenuRef}
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpenUserMenu(!openUserMenu);
                    }}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 12px',
                        backgroundColor: '#FDFDFC',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer'
                    }}
                >
                    <img
                        src="Images/profile-icon-img.png"
                        alt=""
                        style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                        }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: '#1f2937',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {user?.firstName?.toLowerCase()}.{user?.lastName?.toLowerCase()}...
                        </div>
                    </div>
                    <div style={{
                        backgroundColor: '#f3f4f6',
                        borderRadius: '6px',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <UnfoldMoreIcon style={{ fontSize: '16px', color: '#1f2937' }} />
                    </div>
                </div>

                {openUserMenu && createPortal(
                    <div
                        data-user-menu-dropdown
                        style={{
                            position: 'fixed',
                            top: `${menuPosition.top}px`,
                            left: `${menuPosition.left}px`,
                            width: '220px',
                            backgroundColor: '#ffffff',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            border: '1px solid #e5e7eb',
                            overflow: 'hidden',
                            zIndex: 99999
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Item
                            to="/AdminClient/MonProfil"
                            icon={ManageAccountsOutlinedIcon}
                            label={t("Paramètres du compte")}
                            active={is("/AdminClient/MonProfil")}
                            onClick={() => setOpenUserMenu(false)}
                            style={{ borderBottom: '1px solid #f3f4f6', padding: '12px 16px' }}
                        />
                        <Item
                            icon={LogoutOutlinedIcon}
                            label={t("Se déconnecter")}
                            onClick={() => {
                                TokenService.removeUser();
                                dispatch(fetchUser(null));
                                setOpenUserMenu(false);
                            }}
                            className="sb-logout"
                            style={{ padding: '12px 16px' }}
                        />
                    </div>,
                    document.body
                )}
            </div>
            </div>
        </aside>
    );
}
